<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ReportCaptainMessageDialog from './ReportCaptainMessageDialog.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import MessageApi from 'dashboard/api/inbox/message';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  TELEGRAM_REACTIONS,
  REACTION_NAMES,
  displayEmoji,
} from 'dashboard/helper/telegramReactions';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import {
  frequentReactions,
  usedReactions,
  rememberReaction,
} from 'dashboard/helper/frequentReactions';

// Telegram's own list, asked for once per chat and page load.
const reactionsByConversation = {};

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
    ReportCaptainMessageDialog,
    WootMessageEditor,
    TeleportWithDirection,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      showEditModal: false,
      showAllReactions: false,
      allReactions: TELEGRAM_REACTIONS,
      pickerPosition: { x: 0, y: 0 },
      reactionSearch: '',
      editContent: '',
      isSavingEdit: false,
      showTicketModal: false,
      ticketTitle: '',
      isSavingTicket: false,
      showTicketPicker: false,
      openTickets: [],
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
      currentUserId: 'getCurrentUserID',
    }),
    // This operator's most used ones first; the defaults fill the row.
    quickReactions() {
      return frequentReactions(this.currentUserId, this.allReactions);
    },
    // «Часто используемые» in the picker, like Telegram's own: two rows.
    myReactions() {
      return usedReactions(this.currentUserId, this.allReactions).slice(0, 16);
    },
    foundReactions() {
      const query = this.reactionSearch.trim().toLowerCase();
      if (!query) return this.allReactions;
      return this.allReactions.filter(emoji =>
        (REACTION_NAMES[emoji.replace(/\uFE0F/g, '')] || '').includes(query)
      );
    },
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
  },
  watch: {
    // The quick row too shows only what this messenger accepts: Telegram and
    // MAX have different sets, so the list is asked for as the menu opens.
    isOpen(open) {
      if (open && this.enabledOptions.react) this.loadAllReactions();
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    async handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      try {
        await this.$store.dispatch('translateMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          targetLanguage,
        });
        useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      } catch (error) {
        useAlert(parseAPIErrorResponse(error));
      }
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    openReportDialog() {
      this.handleClose();
      this.$refs.reportDialog?.open();
    },
    displayEmoji,
    openAllReactions() {
      // Opens on its own, where the menu was: the menu closes as soon as it
      // loses focus, and its position goes with it.
      const { x = 0, y = 0 } = this.contextMenuPosition || {};
      this.pickerPosition = {
        x: Math.max(8, Math.min(x, window.innerWidth - 310)),
        y: Math.max(8, Math.min(y, window.innerHeight - 500)),
      };
      this.reactionSearch = '';
      this.showAllReactions = true;
      this.handleClose();
      this.$nextTick(this.fitReactionPicker);
      this.loadAllReactions();
    },
    // Moves the picker up until it fits: its height depends on how many
    // frequently used reactions there are, known only once it is drawn.
    fitReactionPicker() {
      const panel = this.$refs.reactionPicker;
      if (!panel) return;
      const bottomRoom = window.innerHeight - 8 - panel.offsetHeight;
      this.pickerPosition = {
        ...this.pickerPosition,
        y: Math.max(8, Math.min(this.pickerPosition.y, bottomRoom)),
      };
    },
    async loadAllReactions() {
      const cached = reactionsByConversation[this.conversationId];
      if (cached) {
        this.allReactions = cached;
        return;
      }
      try {
        const { data } = await MessageApi.telegramReactions(
          this.conversationId
        );
        if (data.reactions?.length) {
          reactionsByConversation[this.conversationId] = data.reactions;
          this.allReactions = data.reactions;
          this.$nextTick(this.fitReactionPicker);
        }
      } catch (error) {
        // The built-in list stays.
      }
    },
    async react(emoji) {
      this.showAllReactions = false;
      this.handleClose();
      try {
        await this.$store.dispatch('reactToMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          emoji,
        });
        rememberReaction(this.currentUserId, emoji);
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            this.$t('CONVERSATION.REACTIONS.FAILED')
        );
      }
    },
    openTicketModal() {
      this.handleClose();
      const text = this.plainTextContent.replace(/\s+/g, ' ').trim();
      this.ticketTitle = text.length > 80 ? `${text.slice(0, 79)}…` : text;
      this.showTicketModal = true;
    },
    async createTicket() {
      this.isSavingTicket = true;
      try {
        await MessageApi.createOperatorTicket(
          this.conversationId,
          this.messageId,
          this.ticketTitle.trim()
        );
        this.showTicketModal = false;
        emitter.emit(BUS_EVENTS.OPERATOR_TICKETS_CHANGED);
        useAlert(this.$t('CONVERSATION.OPERATOR_TICKETS.CREATED'));
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            this.$t('CONVERSATION.OPERATOR_TICKETS.FAILED')
        );
      } finally {
        this.isSavingTicket = false;
      }
    },
    // The chat's open tickets, next to where the menu was.
    async openTicketPicker() {
      this.pickerPosition = {
        x: Math.min(this.contextMenuPosition.x, window.innerWidth - 300),
        y: Math.max(
          8,
          Math.min(this.contextMenuPosition.y, window.innerHeight - 320)
        ),
      };
      this.handleClose();
      this.openTickets = [];
      this.showTicketPicker = true;
      try {
        const { data } = await MessageApi.operatorTickets(this.conversationId);
        this.openTickets = data.payload.filter(t => t.status === 'open');
      } catch (error) {
        this.showTicketPicker = false;
        useAlert(this.$t('CONVERSATION.OPERATOR_TICKETS.FAILED'));
      }
    },
    async addToTicket(ticket) {
      this.showTicketPicker = false;
      try {
        await MessageApi.addToOperatorTicket(
          this.conversationId,
          ticket.id,
          this.messageId
        );
        emitter.emit(BUS_EVENTS.OPERATOR_TICKETS_CHANGED);
        useAlert(
          this.$t('CONVERSATION.OPERATOR_TICKETS.ADDED', {
            title: ticket.title,
          })
        );
      } catch (error) {
        useAlert(this.$t('CONVERSATION.OPERATOR_TICKETS.FAILED'));
      }
    },
    openEditModal() {
      this.handleClose();
      this.editContent = this.messageContent;
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    async confirmEdit() {
      this.isSavingEdit = true;
      try {
        await this.$store.dispatch('editMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          content: this.editContent,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_EDIT_MESSAGE'));
        this.showEditModal = false;
      } catch (error) {
        // The messenger's own reason (e.g. the message is too old to edit)
        // tells the agent more than a generic failure.
        useAlert(
          error.response?.data?.error ||
            this.$t('CONVERSATION.FAIL_EDIT_MESSAGE')
        );
      } finally {
        this.isSavingEdit = false;
      }
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <woot-modal
      v-if="showEditModal && enabledOptions['edit']"
      v-model:show="showEditModal"
      :on-close="closeEditModal"
    >
      <form class="flex flex-col gap-4 p-8" @submit.prevent="confirmEdit">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ $t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.TITLE') }}
        </h2>
        <!-- The same editor as the reply box, so formatting shows as
             formatting rather than as markdown. -->
        <WootMessageEditor
          v-model="editContent"
          class="message-editor min-h-[8rem] rounded-lg border border-n-weak px-3"
          :placeholder="$t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.PLACEHOLDER')"
          :enable-suggestions="false"
          :enable-canned-responses="false"
          override-line-breaks
        />
        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            type="button"
            :label="$t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.CANCEL')"
            @click="closeEditModal"
          />
          <NextButton
            type="submit"
            :is-loading="isSavingEdit"
            :disabled="!editContent.trim()"
            :label="$t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.SAVE')"
          />
        </div>
      </form>
    </woot-modal>
    <woot-modal
      v-if="showTicketModal && enabledOptions['ticket']"
      v-model:show="showTicketModal"
      :on-close="() => (showTicketModal = false)"
    >
      <form class="flex flex-col gap-4 p-8" @submit.prevent="createTicket">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ $t('CONVERSATION.OPERATOR_TICKETS.NEW_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('CONVERSATION.OPERATOR_TICKETS.NEW_HINT') }}
        </p>
        <input
          v-model="ticketTitle"
          type="text"
          maxlength="255"
          class="!mb-0 w-full rounded-lg border border-n-weak px-3 py-2 text-sm"
          :placeholder="$t('CONVERSATION.OPERATOR_TICKETS.TITLE_PLACEHOLDER')"
        />
        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            type="button"
            :label="$t('CONVERSATION.OPERATOR_TICKETS.CANCEL')"
            @click="showTicketModal = false"
          />
          <NextButton
            type="submit"
            :is-loading="isSavingTicket"
            :label="$t('CONVERSATION.OPERATOR_TICKETS.CREATE')"
          />
        </div>
      </form>
    </woot-modal>
    <TeleportWithDirection v-if="showTicketPicker" to="body">
      <div
        v-on-clickaway="() => (showTicketPicker = false)"
        class="fixed z-[9999] flex max-h-80 w-72 flex-col gap-0.5 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-1 shadow-lg"
        :style="{ top: `${pickerPosition.y}px`, left: `${pickerPosition.x}px` }"
      >
        <p class="px-2 py-1 text-xs font-medium text-n-slate-11">
          {{ $t('CONVERSATION.OPERATOR_TICKETS.PICK') }}
        </p>
        <button
          v-for="ticket in openTickets"
          :key="ticket.id"
          type="button"
          class="truncate rounded-md px-2 py-1.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          :title="ticket.title"
          @click.stop="addToTicket(ticket)"
        >
          {{ ticket.title }}
        </button>
        <p v-if="!openTickets.length" class="px-2 py-2 text-xs text-n-slate-11">
          {{ $t('CONVERSATION.OPERATOR_TICKETS.NONE_OPEN') }}
        </p>
      </div>
    </TeleportWithDirection>
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <TeleportWithDirection v-if="showAllReactions" to="body">
      <div
        v-on-clickaway="() => (showAllReactions = false)"
        class="fixed z-[9999]"
        :style="{ top: `${pickerPosition.y}px`, left: `${pickerPosition.x}px` }"
      >
        <!-- Only what Telegram lets a regular account set: any other emoji
             it refuses. Laid out like Telegram's own picker. -->
        <div
          ref="reactionPicker"
          class="flex max-h-[calc(100vh-1rem)] w-[18.5rem] flex-col gap-1 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-2 shadow-lg"
        >
          <input
            v-model="reactionSearch"
            type="search"
            class="!mb-1 w-full rounded-lg border-0 bg-n-alpha-2 px-3 py-1.5 text-sm"
            :placeholder="$t('CONVERSATION.REACTIONS.SEARCH')"
          />
          <template v-if="!reactionSearch && myReactions.length">
            <p class="px-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION.REACTIONS.FREQUENT') }}
            </p>
            <div class="grid grid-cols-8 gap-0.5">
              <button
                v-for="emoji in myReactions"
                :key="`my-${emoji}`"
                type="button"
                class="rounded-md py-1 text-lg hover:bg-n-alpha-2"
                @click.stop="react(emoji)"
              >
                {{ displayEmoji(emoji) }}
              </button>
            </div>
            <p class="px-1 pt-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION.REACTIONS.ALL') }}
            </p>
          </template>
          <div class="grid grid-cols-8 gap-0.5">
            <button
              v-for="emoji in foundReactions"
              :key="emoji"
              type="button"
              class="rounded-md py-1 text-lg hover:bg-n-alpha-2"
              @click.stop="react(emoji)"
            >
              {{ displayEmoji(emoji) }}
            </button>
          </div>
          <p
            v-if="!foundReactions.length"
            class="px-1 py-2 text-center text-xs text-n-slate-11"
          >
            {{ $t('CONVERSATION.REACTIONS.NOTHING_FOUND') }}
          </p>
        </div>
      </div>
    </TeleportWithDirection>
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <div
          v-if="enabledOptions['react']"
          class="flex items-center gap-0.5 px-1 pb-1 mb-1 border-b border-n-weak"
        >
          <!-- mousedown.prevent: taking focus would close the menu before
               the click lands. -->
          <button
            v-for="emoji in quickReactions"
            :key="emoji"
            type="button"
            class="rounded-md px-1 py-0.5 text-lg hover:bg-n-alpha-2"
            @mousedown.prevent
            @click.stop="react(emoji)"
          >
            {{ displayEmoji(emoji) }}
          </button>
          <NextButton
            ghost
            slate
            xs
            icon="i-lucide-plus"
            :title="$t('CONVERSATION.REACTIONS.MORE')"
            @mousedown.prevent
            @click.stop="openAllReactions"
          />
        </div>
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="enabledOptions['edit']"
          :option="{
            icon: 'edit',
            label: $t('CONVERSATION.CONTEXT_MENU.EDIT'),
          }"
          variant="icon"
          @click.stop="openEditModal"
        />
        <MenuItem
          v-if="enabledOptions['ticket']"
          :option="{
            icon: 'add-circle',
            label: $t('CONVERSATION.OPERATOR_TICKETS.MENU_CREATE'),
          }"
          variant="icon"
          @click.stop="openTicketModal"
        />
        <MenuItem
          v-if="enabledOptions['ticket']"
          :option="{
            icon: 'list',
            label: $t('CONVERSATION.OPERATOR_TICKETS.MENU_ADD'),
          }"
          variant="icon"
          @click.stop="openTicketPicker"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <hr />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="enabledOptions['report']" />
        <MenuItem
          v-if="enabledOptions['report']"
          :option="{
            icon: 'warning',
            label: $t('CONVERSATION.CONTEXT_MENU.REPORT_MESSAGE.LABEL'),
          }"
          variant="icon"
          @click.stop="openReportDialog"
        />
        <hr v-if="enabledOptions['delete']" />
        <MenuItem
          v-if="enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
      </div>
    </ContextMenu>
    <ReportCaptainMessageDialog
      v-if="enabledOptions['report']"
      ref="reportDialog"
      :message-id="messageId"
    />
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}

.context-menu--delete-modal {
  :deep(.modal-container) {
    @apply max-w-[30rem];

    h2 {
      @apply font-medium text-base;
    }
  }
}
</style>
